<script setup lang="ts">
import { Minus, Plus } from "@iconoir/vue";

const model = defineModel<number>({ required: true });

const props = defineProps<{
  min?: number;
  max?: number;
  name: string;
}>();

function decrement() {
  if (props.min !== undefined && model.value <= props.min) return;
  model.value--;
}

function increment() {
  if (props.max !== undefined && model.value >= props.max) return;
  model.value++;
}

function onInput(event: Event) {
  const value = (event.target as HTMLInputElement).valueAsNumber;
  if (!Number.isNaN(value)) {
    model.value = value;
  }
}
</script>

<template>
  <div class="counter-input">
    <button
      class="button"
      type="button"
      aria-label="Verringern"
      :disabled="min !== undefined && model <= min"
      @click="decrement()"
    >
      <Minus aria-hidden="true" />
    </button>
    <input
      :id="name"
      type="number"
      :name="name"
      :min="min"
      :max="max"
      :value="model"
      required
      :class="{ 'no-max': max === undefined }"
      @input="onInput"
    />
    <span v-if="max !== undefined" class="max-value" aria-hidden="true"
      >/{{ max }}</span
    >

    <button
      class="button"
      type="button"
      aria-label="Erhöhen"
      :disabled="max !== undefined && model >= max"
      @click="increment()"
    >
      <Plus aria-hidden="true" />
    </button>
  </div>
</template>

<style scoped>
.counter-input {
  display: flex;
  align-items: stretch;
  .max-value {
    background-color: var(--bg-color-input);
    color: var(--text-color-muted);
    padding-right: var(--spacing-xs);
    display: flex;
    align-items: center;
  }
  .no-max {
    padding-inline: var(--spacing-md);
  }
  button {
    color: var(--text-color-body);
    inset-block: 0;
    box-shadow: none;
    padding: 7.5px;
    svg {
      block-size: var(--size-md);
      inline-size: var(--size-md);
    }
    &:first-of-type {
      inset-inline-start: 0;
      border-radius: var(--border-radius-lg) 0 0 var(--border-radius-lg);
    }
    &:last-of-type {
      inset-inline-end: 0;
      border-radius: 0 var(--border-radius-lg) var(--border-radius-lg) 0;
    }
  }

  input,
  .max-value {
    font: inherit;
    line-height: 1.2;
    padding-block: 9.5px;
  }

  input {
    inline-size: unset;
    field-sizing: content;
    padding-inline: var(--spacing-xs) 0;
    min-inline-size: 2ch;
    text-align: center;
    -moz-appearance: textfield;
    appearance: textfield;
    border-radius: unset;

    ::-webkit-inner-spin-button,
    ::-webkit-outer-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
  }
}
</style>
